import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Typography, CircularProgress, FormControl, InputLabel,
  Select, MenuItem, Chip, Tabs, Tab, Avatar, Alert, Tooltip
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import PeopleIcon from '@mui/icons-material/People';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FlightIcon from '@mui/icons-material/Flight';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useBranch } from '../context/BranchContext';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';
import SupervisorTimecardReview from './SupervisorTimecardReview';
import SupervisorVacationReview from './SupervisorVacationReview';
import ScheduleManager from './ScheduleManager';
import BranchStaffing from './BranchStaffing';
import TrainingCompliance from './TrainingCompliance';

export default function SupervisionPanel() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const subTabKeys = ['employees', 'schedules', 'branch_staffing', 'timecards', 'vacations', 'training_compliance'];
  const subTabKey = searchParams.get('sub') || 'employees';
  const subTab = subTabKeys.indexOf(subTabKey) >= 0 ? subTabKeys.indexOf(subTabKey) : 0;
  const [employees, setEmployees] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const { branches: availableBranches } = useBranch();
  const [branchFilter, setBranchFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_ENDPOINTS.BASEURL}/api/hirees/supervised`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setEmployees(data || []);
          setFiltered(data || []);
          setErrorMsg(null);
        } else if (res.status === 403) {
          try {
            const meRes = await fetch(`${API_ENDPOINTS.BASEURL}/api/user/me`, { credentials: 'include' });
            if (meRes.ok) {
              const me = await meRes.json();
              if (['admin', 'superadmin'].includes(me.role)) {
                const detailsRes = await fetch(`${API_ENDPOINTS.BASEURL}/api/hirees/details`, { credentials: 'include' });
                if (detailsRes.ok) {
                  const d = await detailsRes.json();
                  setEmployees(d || []);
                  setFiltered(d || []);
                  setErrorMsg(null);
                } else {
                  setEmployees([]);
                  setFiltered([]);
                  setErrorMsg(t('failed_loading_employees') || 'Failed loading employees');
                }
              } else {
                setEmployees([]);
                setFiltered([]);
                setErrorMsg(t('not_authorized_to_view_supervised') || 'You are not authorized to view supervised employees');
              }
            } else {
              setEmployees([]);
              setFiltered([]);
              setErrorMsg(t('failed_loading_user') || 'Failed to load current user info');
            }
          } catch (fallbackErr) {
            console.error('Fallback fetch error:', fallbackErr);
            setEmployees([]);
            setFiltered([]);
            setErrorMsg(t('failed_loading_employees') || 'Failed loading employees');
          }
        } else {
          setEmployees([]);
          setFiltered([]);
          setErrorMsg(t('failed_loading_employees') || 'Failed loading employees');
        }
      } catch (err) {
        console.error('Error fetching supervised hirees:', err);
        setEmployees([]);
        setFiltered([]);
        setErrorMsg(t('failed_loading_employees') || 'Failed loading employees');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hexToRgb = (hex) => {
    if (!hex) return null;
    let c = hex.replace('#', '').trim();
    if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
    if (c.length !== 6) return null;
    const num = parseInt(c, 16);
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
  };

  const getContrastColor = (hex) => {
    const rgb = hexToRgb(hex || '');
    if (!rgb) return '#000';
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b);
    return luminance > 186 ? '#000' : '#fff';
  };

  const getBranchInfo = (branchId) => {
    if (!availableBranches) return null;
    return availableBranches.find(b => (b.branch_id || b.code || b._id) === branchId) || null;
  };

  const handleSearch = (e) => {
    const q = e.target.value.toLowerCase();
    setSearch(q);
  };

  useEffect(() => {
    let list = employees || [];
    if (branchFilter) {
      list = list.filter(emp => (emp.branches || []).includes(branchFilter));
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(emp => (
        (emp.fullname || '').toLowerCase().includes(q) ||
        (emp.email || '').toLowerCase().includes(q) ||
        (emp.position || '').toLowerCase().includes(q)
      ));
    }
    setFiltered(list);
  }, [employees, search, branchFilter]);

  const handleSubTabChange = (_, newValue) => {
    setSearchParams(prev => {
      prev.set('sub', subTabKeys[newValue]);
      return prev;
    });
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4 } }}>
      {/* Page Header */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {t('supervision_panel') || 'Supervision'}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640 }}>
          {t('supervision_subtitle') || 'Manage your team\'s employees, timecards, schedules, and vacation requests.'}
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={subTab}
          onChange={handleSubTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 48 }}
        >
          <Tab icon={<PeopleIcon />} iconPosition="start" label={t('hiree_panel') || 'Employees'} sx={{ textTransform: 'none', minHeight: 48 }} />
          <Tab icon={<CalendarMonthIcon />} iconPosition="start" label={t('calendar_schedule_title') || 'Schedules'} sx={{ textTransform: 'none', minHeight: 48 }} />
          <Tab icon={<AssignmentIcon />} iconPosition="start" label={t('branch_staffing_tab') || 'Branch Staffing'} sx={{ textTransform: 'none', minHeight: 48 }} />
          <Tab icon={<AccessTimeIcon />} iconPosition="start" label={t('calendar_supervisor_review') || 'Timecards'} sx={{ textTransform: 'none', minHeight: 48 }} />
          <Tab icon={<FlightIcon />} iconPosition="start" label={t('vacation_supervisor_tab') || 'Vacations'} sx={{ textTransform: 'none', minHeight: 48 }} />
          <Tab icon={<AssignmentIcon />} iconPosition="start" label={t('training_compliance_title') || 'Training Compliance'} sx={{ textTransform: 'none', minHeight: 48 }} />
        </Tabs>
      </Paper>

      {/* Employees Tab */}
      {subTab === 0 && (
        <>
          {/* Search & Filter Bar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
            <TextField
              size="small"
              variant="outlined"
              placeholder={t('search') || 'Search employees...'}
              value={search}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <FilterListIcon fontSize="small" sx={{ color: '#62B6CB', mr: 0.5 }} />
              }}
              sx={{ minWidth: { xs: '100%', sm: 200 }, flex: 1, maxWidth: 350 }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 }, maxWidth: 250 }}>
              <InputLabel>{t('filter_by_branch') || 'Branch'}</InputLabel>
              <Select
                value={branchFilter}
                label={t('filter_by_branch') || 'Branch'}
                onChange={(e) => setBranchFilter(e.target.value)}
              >
                <MenuItem value="">{t('all_branches') || 'All branches'}</MenuItem>
                {availableBranches.map((b) => (
                  <MenuItem key={b.branch_id || b.code || b._id} value={b.branch_id || b.code || b._id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {filtered.length > 0 && (
              <Chip
                label={`${filtered.length} employee${filtered.length !== 1 ? 's' : ''}`}
                size="small"
                sx={{ height: 24, fontWeight: 500 }}
              />
            )}
          </Box>

          {/* Loading State */}
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading employees...
              </Typography>
            </Box>
          ) : errorMsg ? (
            /* Error State */
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {errorMsg}
            </Alert>
          ) : filtered.length === 0 ? (
            /* Empty State */
            <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
              <Avatar sx={{ width: 64, height: 64, mb: 2, bgcolor: 'grey.100', color: 'grey.400' }}>
                <PeopleIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {t('no_employees_found') || 'No employees found'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search or filter criteria.
              </Typography>
            </Box>
          ) : (
            /* Employee Table */
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider', overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 560 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>
                      {t('name') || 'Employee'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>
                      {t('position') || 'Position'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>
                      {t('status') || 'Status'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>
                      {t('branches') || 'Branches'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((emp) => (
                    <TableRow
                      key={emp.email}
                      hover
                      sx={{ '&:last-child td': { border: 0 }, '& .MuiTableCell-root': { py: 1.5 } }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: 'primary.main',
                              fontSize: 14,
                              fontWeight: 600
                            }}
                          >
                            {(emp.fullname || emp.email).charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
                              {emp.fullname || emp.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {emp.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{emp.position}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={emp.status}
                          size="small"
                          color={emp.status === 'active' ? 'success' : 'default'}
                          sx={{
                            height: 24,
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {(emp.branches || []).map((bid) => {
                            const br = getBranchInfo(bid) || {};
                            const color = (br.config && br.config.color) || br.color || '#e0e0e0';
                            const label = br.name || bid;
                            return (
                              <Tooltip key={bid} title={label}>
                                <Chip
                                  label={label}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    backgroundColor: color,
                                    color: getContrastColor(color)
                                  }}
                                />
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Schedules Tab */}
      {subTab === 1 && (
        <ScheduleManager hirees={employees} />
      )}

      {/* Branch Staffing Tab */}
      {subTab === 2 && (
        <BranchStaffing employees={employees} />
      )}

      {/* Timecards Tab */}
      {subTab === 3 && (
        <SupervisorTimecardReview />
      )}

      {/* Vacations Tab */}
      {subTab === 4 && (
        <SupervisorVacationReview />
      )}

      {/* Training Compliance Tab */}
      {subTab === 5 && (
        <TrainingCompliance />
      )}
    </Box>
  );
}
